import { Injectable } from '@angular/core';
import {
  NextWorkingDayService,
  NextWorkingDayRequest,
  OrderActionType,
  ILegalEntity,
  COUNTRY_CODE,
} from '@cemex-core/cmx-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store, createSelector } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take, catchError, tap } from 'rxjs/operators';
import { getOrderBuilderState, OrderBuilderFeatureState, OrderBuilderState } from '../../../reducers';
import { RmsSpecificationActionTypes } from '../actions/rms-specification.actions';
import { PRODUCT_LINE_CODE } from '@ordersnproducts/modules/shared/constants/product-lines-codes';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import {
  CutOffLoadAction,
  CutOffActionTypes,
  CutOffLoadCompleteAction,
  CutOffResetAction,
  CutOffLoadActionV5,
} from '../actions/cut-off-actions';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { RmsNextWorkingDayResponse } from '@cemex-core/cmx-api/models/next-working-day/next-working-day-res';
import { RESET_BUILDER } from '../../../actions/order-builder.actions';
import { CutOffTemperatureResetAction } from '../actions/cut-off-temperature.actions';

export const getCustomerAndOrderBuilderState = createSelector(
  getLegalEntity,
  getOrderBuilderState,
  (customer, orderBuilderState) => ({
    legalEntity: customer,
    state: orderBuilderState,
  }),
);

@Injectable()
export class CutOffEffects {
  loadContractProduct$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RmsSpecificationActionTypes.INIT),
        tap(() => {
          this.store
            .select(getCustomerAndOrderBuilderState)
            .pipe(
              filter((data) => !!data),
              take(1),
            )
            .subscribe((data) => {
              const isV5 = this.isV5Applicable(data.legalEntity.countryCode.trim());
              if (isV5) {
                this.store.dispatch(new CutOffLoadActionV5());
              } else {
                this.store.dispatch(new CutOffLoadAction());
              }
            });
        }),
      ),
    { dispatch: false },
  );

  getCutOff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CutOffActionTypes.LOAD),
      switchMap((action: CutOffLoadAction) => {
        return this.store.select(getCustomerAndOrderBuilderState).pipe(
          take(1),
          filter((data) => !!data),
          map((data) => this.makeRmsNextWorkingDayRequest(data.legalEntity, data.state)),
          switchMap((request: NextWorkingDayRequest) =>
            this.nextWorkingDayService.getRmsNextWorkingDay(request).pipe(
              map((result: RmsNextWorkingDayResponse) => new CutOffLoadCompleteAction(result)),
              catchError((error) => {
                return of(new CutOffResetAction());
              }),
            ),
          ),
        );
      }),
    ),
  );

  getCutOffV5$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CutOffActionTypes.LOAD_V5),
      switchMap((action: CutOffLoadActionV5) => {
        return this.store.select(getCustomerAndOrderBuilderState).pipe(
          filter((data) => !!data),
          take(1),
          map((data) => this.makeRmsNextWorkingDayRequestV5(data.legalEntity, data.state)),
          switchMap((request: NextWorkingDayRequest) => {
            return this.nextWorkingDayService.getNextWorkingDayV5(request).pipe(
              map((result) => {
                const cutOff = result.timeCut;
                let timeCut = '';
                if (cutOff[0] === ':') {
                  timeCut = cutOff.substring(1);
                } else {
                  timeCut = cutOff.replace(/:([0-9]{2}):([0-9]{2}):00./, ':$1:$2.');
                }
                return {
                  ...result,
                  timeCut: timeCut,
                };
              }),
              map((result) => new CutOffLoadCompleteAction(result)),
              catchError((error) => {
                return of(new CutOffResetAction());
              }),
            );
          }),
        );
      }),
    ),
  );

  cutoffTemperatureReset$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RESET_BUILDER),
      map(() => new CutOffTemperatureResetAction()),
    ),
  );

  /**
   *
   * @param {OrderBuilderFeatureState} state
   * @param {ILegalEntity} legalEntity
   * @return {NextWorkingDayRequest}
   */
  private makeRmsNextWorkingDayRequest(
    legalEntity: ILegalEntity,
    state: OrderBuilderFeatureState,
  ): NextWorkingDayRequest {
    return {
      productLineCode: _safe(() => state.businessLinePage.businessLine.selected.productTypeCode as PRODUCT_LINE_CODE),
      countryCode: legalEntity.countryCode,
      action: OrderActionType.VERIFICATION_CREATE,
      shipmentLocationCode: state.locationPage.jobsite.selected.shipmentLocationCode,
    } as NextWorkingDayRequest;
  }

  /**
   *
   * @param {OrderBuilderFeatureState} state
   * @param {ILegalEntity} legalEntity
   * @return {NextWorkingDayRequest}
   */
  private makeRmsNextWorkingDayRequestV5(
    legalEntity: ILegalEntity,
    state: OrderBuilderFeatureState,
  ): NextWorkingDayRequest {
    return {
      productLineCode: _safe(() => state.businessLinePage.businessLine.selected.productTypeCode as PRODUCT_LINE_CODE),
      countryCode: legalEntity.countryCode,
      action: OrderActionType.VERIFICATION_CREATE,
      jobsiteCode: state.locationPage.jobsite.selected.shipmentLocationCode,
    } as NextWorkingDayRequest;
  }

  /**
   * Determines whether to use version 5 based on the provided country code.
   *
   * @param countryCode - The country code to check.
   * @returns `true` if the country code is in the list of countries that use version 5, otherwise `false`.
   */
  private isV5Applicable(countryCode: string): boolean {
    const countries: string[] = [COUNTRY_CODE.USA, COUNTRY_CODE.FRANCE];
    return countries.includes(countryCode);
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private nextWorkingDayService: NextWorkingDayService,
  ) {}
}
